import { useState } from "react";
import { X } from "lucide-react";
import { adminClient, formatApiError } from "@/admin/api";

const inputCls =
  "mt-2 w-full bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm px-4 py-3 font-sans text-sm text-[#1A1715] focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C]";
const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.18em] text-[#44403C] font-semibold";

const ChangePassword = ({ onClose, onChanged }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setSaving(true);
    try {
      await adminClient().post("/admin/change-password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      setDone(true);
    } catch (err) {
      if (err.response?.status === 401) {
        onChanged();
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="change-password-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1715]/50 px-5"
      role="dialog"
      aria-modal="true"
      aria-label="Change password"
    >
      <div className="w-full max-w-md bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm p-7 shadow-[0_24px_60px_rgba(26,23,21,0.25)]">
        <div className="flex items-center justify-between">
          <h2 className="font-serif font-semibold text-xl text-[#1A1715]">
            Change Password
          </h2>
          <button
            type="button"
            data-testid="change-password-close"
            onClick={onClose}
            aria-label="Close"
            className="text-[#A8A29E] hover:text-[#1A1715] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="mt-6">
            <p
              data-testid="change-password-success"
              className="border border-[#BBF7D0] bg-[#F0FDF4] rounded-sm px-4 py-3 font-sans text-sm font-medium text-[#166534]"
            >
              Password updated. For security, all sessions were signed out —
              please sign in with your new password.
            </p>
            <button
              type="button"
              data-testid="change-password-done-button"
              onClick={onChanged}
              className="mt-6 w-full bg-[#1A1715] text-[#FBF9F5] font-sans font-semibold text-sm px-6 py-3 rounded-sm transition-colors duration-300 hover:bg-[#C2410C]"
            >
              Go to Sign In
            </button>
          </div>
        ) : (
          <form data-testid="change-password-form" onSubmit={submit} className="mt-6">
            {error && (
              <p
                data-testid="change-password-error"
                role="alert"
                className="mb-5 border border-[#FECACA] bg-[#FEF2F2] rounded-sm px-3.5 py-2.5 font-sans text-sm font-medium text-[#B91C1C]"
              >
                {error}
              </p>
            )}
            <div>
              <label htmlFor="cp-current" className={labelCls}>
                Current Password
              </label>
              <input
                id="cp-current"
                data-testid="current-password-input"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className={inputCls}
                required
              />
            </div>
            <div className="mt-5">
              <label htmlFor="cp-new" className={labelCls}>
                New Password
              </label>
              <input
                id="cp-new"
                data-testid="new-password-input"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className={inputCls}
                required
              />
            </div>
            <div className="mt-5">
              <label htmlFor="cp-confirm" className={labelCls}>
                Confirm New Password
              </label>
              <input
                id="cp-confirm"
                data-testid="confirm-password-input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputCls}
                required
              />
            </div>
            <p className="mt-4 font-sans text-xs text-[#78716C] leading-relaxed">
              Minimum 12 characters with uppercase, lowercase, a number, and a
              symbol. Changing your password signs out all sessions.
            </p>
            <button
              type="submit"
              data-testid="change-password-submit"
              disabled={saving}
              className="mt-6 w-full bg-[#C2410C] text-[#FBF9F5] font-sans font-semibold text-sm px-6 py-3 rounded-sm transition-colors duration-300 hover:bg-[#9A3412] disabled:opacity-60"
            >
              {saving ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;
